import { useCallback, useEffect, useMemo, useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Settings,
  Save,
  RefreshCw,
  Bell,
  Shield,
  Database,
  BarChart3,
  Wifi,
  Printer,
  CreditCard,
  Palette,
  Moon,
  Sun,
  Monitor,
  DollarSign,
  Volume2,
  Zap,
  Lock,
  Clock,
  Mail,
  Phone,
  MapPin,
  Tag,
  Users,
  CheckCircle2,
  AlertTriangle,
  Smartphone,
  Cloud,
  FileText,
  Download,
  Settings2,
  QrCode,
  Building2,
  Sparkles,
  ChevronRight,
  Info,
  Loader2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTheme } from "@/components/ThemeProvider";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  updateRestaurantSettings,
  type TaxMode,
  type RestaurantSettings as RemoteRestaurantSettings,
  type UpdateRestaurantPayload,
  fetchPrinters,
  type PrinterRecord,
} from "@/lib/api/settings";
import {
  checkForSystemUpdates,
  clearSystemCaches,
  fetchSecurityOverview,
  runSystemDiagnostics,
  type SystemDiagnosticsResponse,
  type SystemUpdateCheckResponse,
} from "@/lib/api/system";
import { fetchNotificationSummary } from "@/lib/api/notifications";
import { useTenantStore } from "@/stores/tenantStore";
import { useRestaurantSettings, RESTAURANT_SETTINGS_QUERY_KEY } from "@/features/restaurant/hooks/useRestaurantSettings";
import { FinancialProfilesManager } from "@/features/restaurant/components/settings/FinancialProfilesManager";
import { LoyaltyProgramManager } from "@/features/restaurant/components/settings/LoyaltyProgramManager";
import HandheldDeviceManager, { type HandheldDevice } from "@/features/handheld/admin/HandheldDeviceManager";
import {
  listHandheldDevices,
  provisionHandheldDevice,
  rotateHandheldDevice,
  revokeHandheldDevice,
} from "@/lib/api/handheld";

type SettingsState = {
  notifications: boolean;
  autoRefresh: boolean;
  soundAlerts: boolean;
  printerEnabled: boolean;
  backupEnabled: boolean;
  emailNotifications: boolean;
  smsAlerts: boolean;
  darkModeSchedule: boolean;
  restaurantName: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  timezone: string;
  currency: string;
  taxRate: string;
  serviceCharge: string;
  cardSurcharge: string;
  taxMode: TaxMode;
};

type BooleanSettingKey =
  | "notifications"
  | "autoRefresh"
  | "soundAlerts"
  | "printerEnabled"
  | "backupEnabled"
  | "emailNotifications"
  | "smsAlerts"
  | "darkModeSchedule";

type StringSettingKey =
  | "restaurantName"
  | "contactEmail"
  | "contactPhone"
  | "address"
  | "timezone"
  | "currency"
  | "taxRate"
  | "serviceCharge"
  | "cardSurcharge";

type StatusCardBadge = {
  label: string;
  className?: string;
};

type StatusCardConfig = {
  key: "tax-mode" | "currency" | "notifications" | "printers";
  label: string;
  value: string;
  helperText?: string;
  icon: LucideIcon;
  gradientClass: string;
  iconContainerClass: string;
  iconClass: string;
  badges?: StatusCardBadge[];
};

type ThemeOption = {
  value: "light" | "dark" | "system";
  icon: LucideIcon;
  title: string;
  description: string;
};

const defaultSettings: SettingsState = {
  notifications: true,
  autoRefresh: true,
  soundAlerts: true,
  printerEnabled: true,
  backupEnabled: true,
  emailNotifications: true,
  smsAlerts: false,
  darkModeSchedule: false,
  restaurantName: "The Gourmet Table",
  contactEmail: "contact@gourmet-table.com",
  contactPhone: "+1 (555) 123-4567",
  address: "123 Main Street, New York, NY 10001",
  timezone: "America/New_York",
  currency: "USD",
  taxRate: "8.5",
  serviceCharge: "0",
  cardSurcharge: "0",
  taxMode: "exclusive",
};

const booleanSettingLabels: Record<BooleanSettingKey, string> = {
  notifications: "Notifications",
  autoRefresh: "Auto Refresh",
  soundAlerts: "Sound Alerts",
  printerEnabled: "Kitchen Printer",
  backupEnabled: "Auto Backup",
  emailNotifications: "Email Notifications",
  smsAlerts: "SMS Alerts",
  darkModeSchedule: "Dark Mode Schedule",
};

const themeOptions: ThemeOption[] = [
  {
    value: "light",
    icon: Sun,
    title: "Light",
    description: "Bright interface for daytime shifts",
  },
  {
    value: "dark",
    icon: Moon,
    title: "Dark",
    description: "Low-light friendly palette",
  },
  {
    value: "system",
    icon: Monitor,
    title: "System",
    description: "Follow device preference",
  },
];

const timezoneOptions = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
];

const HANDHELD_DEVICES_QUERY_KEY = ["handheld", "devices"] as const;

const currencyOptions = [
  { value: "USD", label: "USD ($)" },
  { value: "EUR", label: "EUR (\u20AC)" },
  { value: "GBP", label: "GBP (\u00A3)" },
  { value: "JPY", label: "JPY (\u00A5)" },
];

const PRINTER_STATUS_DOT: Record<PrinterRecord["status"], string> = {
  online: "bg-emerald-500",
  offline: "bg-red-500",
  unknown: "bg-amber-500",
};

const percentToInputString = (value: number | null | undefined): string => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "0";
  }
  const fixed = Number(value.toFixed(2));
  return Number.isInteger(fixed) ? fixed.toString() : fixed.toString();
};

const parsePercentInput = (value: string): number => {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  if (parsed > 100) {
    return 100;
  }
  return parsed;
};

const parseSurchargeInput = (value: string): number => {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  if (parsed > 4) {
    return 4;
  }
  return parsed;
};

const sanitizeOptionalString = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const buildUpdatePayload = (state: SettingsState): UpdateRestaurantPayload => {
  const trimmedName = state.restaurantName.trim();
  const normalizedCurrency = state.currency.trim().toUpperCase() || defaultSettings.currency;

  return {
    name: trimmedName || defaultSettings.restaurantName,
    timezone: state.timezone,
    taxMode: state.taxMode,
    contactEmail: sanitizeOptionalString(state.contactEmail),
    contactPhone: sanitizeOptionalString(state.contactPhone),
    address: sanitizeOptionalString(state.address),
    currency: normalizedCurrency,
    taxRate: parsePercentInput(state.taxRate),
    serviceCharge: parsePercentInput(state.serviceCharge),
    cardSurcharge: parseSurchargeInput(state.cardSurcharge),
    notifications: state.notifications,
    autoRefresh: state.autoRefresh,
    soundAlerts: state.soundAlerts,
    printerEnabled: state.printerEnabled,
    backupEnabled: state.backupEnabled,
    emailNotifications: state.emailNotifications,
    smsAlerts: state.smsAlerts,
    darkModeSchedule: state.darkModeSchedule,
  };
};

const formatUptimeDuration = (seconds?: number | null): string => {
  if (!Number.isFinite(seconds ?? NaN) || (seconds ?? 0) <= 0) {
    return "Not available";
  }
  const totalSeconds = Math.max(0, Math.floor(seconds ?? 0));
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const formatRelativeTime = (value?: string | number | Date | null): string => {
  if (!value) {
    return "Not available";
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Not available";
  }
  return formatDistanceToNow(date, { addSuffix: true });
};

const SettingsPageV3 = () => {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<SettingsState>(() => ({ ...defaultSettings }));
  const [restaurantDialogOpen, setRestaurantDialogOpen] = useState(false);
  const [paymentsDialogOpen, setPaymentsDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [loyaltyDialogOpen, setLoyaltyDialogOpen] = useState(false);
const [financialProfilesDialogOpen, setFinancialProfilesDialogOpen] = useState(false);
  const [handheldDialogOpen, setHandheldDialogOpen] = useState(false);
  const [newHandheldName, setNewHandheldName] = useState("");
  const [newHandheldLocation, setNewHandheldLocation] = useState("");
  const [hasHydrated, setHasHydrated] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastUpdateCheck, setLastUpdateCheck] = useState<SystemUpdateCheckResponse | null>(null);
  const queryClient = useQueryClient();
  const { subdomain: routeSubdomain } = useParams<{ subdomain?: string }>();
  const tenantStore = useTenantStore();
  const tenantSlug =
    routeSubdomain ??
    tenantStore.activeSubdomain ??
    tenantStore.slug ??
    (typeof window !== "undefined" ? localStorage.getItem("tenantSlug") ?? undefined : undefined);
  const handheldDevicesQueryKey = useMemo(
    () => [...HANDHELD_DEVICES_QUERY_KEY, tenantStore.activeRestaurantId ?? "tenantless"] as const,
    [tenantStore.activeRestaurantId],
  );
  const systemDiagnosticsQueryKey = useMemo(
    () => ["settings", "system-diagnostics", tenantStore.activeRestaurantId ?? "tenantless"] as const,
    [tenantStore.activeRestaurantId],
  );
  const securityOverviewQueryKey = useMemo(
    () => ["settings", "security-overview", tenantStore.activeRestaurantId ?? "tenantless"] as const,
    [tenantStore.activeRestaurantId],
  );
  const notificationSummaryQuery = useQuery({
    queryKey: ["notifications", "summary", tenantSlug],
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        throw new Error("missing tenant slug");
      }
      return fetchNotificationSummary(tenantSlug, { signal });
    },
    enabled: Boolean(tenantSlug),
    staleTime: 60_000,
  });
  const notificationSummary = notificationSummaryQuery.data;
  const {
    settings: serverSettings,
    preferences,
    isLoading: settingsLoading,
    isError: settingsError,
    error: settingsErrorObject,
    invalidate: invalidateRestaurantSettings,
  } = useRestaurantSettings();
  const systemDiagnosticsQuery = useQuery({
    queryKey: systemDiagnosticsQueryKey,
    queryFn: ({ signal }) => runSystemDiagnostics(signal),
    enabled: Boolean(serverSettings?.id),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const securityOverviewQuery = useQuery({
    queryKey: securityOverviewQueryKey,
    queryFn: ({ signal }) => fetchSecurityOverview(signal),
    enabled: Boolean(serverSettings?.id),
    refetchInterval: 5 * 60_000,
  });
  const printersQuery = useQuery({
    queryKey: ["settings", "printers"],
    queryFn: ({ signal }) => fetchPrinters(signal),
    enabled: Boolean(serverSettings?.id),
    staleTime: 30_000,
  });
  const handheldDevicesQuery = useQuery({
    queryKey: handheldDevicesQueryKey,
    queryFn: () => listHandheldDevices(),
    enabled: Boolean(tenantStore.activeRestaurantId),
    staleTime: 30_000,
  });
  const handheldDevices = useMemo<HandheldDevice[]>(() => {
    const devices = handheldDevicesQuery.data ?? [];
    return devices.map((device) => ({
      id: device.id,
      label: device.name,
      locationName: device.locationName,
      status: device.status,
      heartbeatAt: device.lastSeenAt ? new Date(device.lastSeenAt) : null,
      tokenRotatedAt: device.tokenRotatedAt ? new Date(device.tokenRotatedAt) : null,
      tokenLastFour: device.tokenLastFour,
    }));
  }, [handheldDevicesQuery.data]);
  const provisionHandheldMutation = useMutation({
    mutationFn: provisionHandheldDevice,
    onSuccess: ({ token }) => {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        navigator.clipboard.writeText(token).catch(() => {});
      }
      toast.success("Handheld device provisioned", {
        description: `Copy immediately: ${token}`,
      });
      setHandheldDialogOpen(false);
      setNewHandheldName("");
      setNewHandheldLocation("");
      void queryClient.invalidateQueries({ queryKey: handheldDevicesQueryKey });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Unable to provision handheld device.";
      toast.error("Handheld device provisioning failed", { description: message });
    },
  });
  const rotateHandheldMutation = useMutation({
    mutationFn: (deviceId: string) => rotateHandheldDevice(deviceId),
    onSuccess: ({ token }) => {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        navigator.clipboard.writeText(token).catch(() => {});
      }
      toast.success("Handheld token rotated", {
        description: `Copy immediately: ${token}`,
      });
      void queryClient.invalidateQueries({ queryKey: handheldDevicesQueryKey });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Unable to rotate handheld token.";
      toast.error("Rotation failed", { description: message });
    },
  });
  const revokeHandheldMutation = useMutation({
    mutationFn: (deviceId: string) => revokeHandheldDevice(deviceId),
    onSuccess: () => {
      toast.success("Handheld device revoked");
      void queryClient.invalidateQueries({ queryKey: handheldDevicesQueryKey });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Unable to revoke handheld device.";
      toast.error("Revocation failed", { description: message });
    },
  });
  const clearCacheMutation = useMutation({
    mutationFn: () => clearSystemCaches(),
    onSuccess: (response) => {
      toast.success("Cache cleared", {
        description: response.message ?? "Recommendation and recipe caches reset.",
      });
      void systemDiagnosticsQuery.refetch();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Unable to clear caches.";
      toast.error("Cache clear failed", { description: message });
    },
  });
  const runDiagnosticsMutation = useMutation({
    mutationFn: () => runSystemDiagnostics(),
    onSuccess: (diagnostics) => {
      queryClient.setQueryData<SystemDiagnosticsResponse>(systemDiagnosticsQueryKey, diagnostics);
      const summary = diagnostics.issues.length > 0
        ? diagnostics.issues[0]
        : `Latency ${diagnostics.metrics.dbLatencyMs}ms · ${diagnostics.metrics.offlinePrinters} offline`;
      toast.success("Diagnostics complete", { description: summary });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Unable to run diagnostics.";
      toast.error("Diagnostics failed", { description: message });
    },
  });
  const updateCheckMutation = useMutation({
    mutationFn: () => checkForSystemUpdates(),
    onSuccess: (data) => {
      setLastUpdateCheck(data);
      const checkedAtCopy = formatDistanceToNow(new Date(data.checkedAt), { addSuffix: true });
      const description = data.updateAvailable
        ? `Latest ${data.latestVersion} published ${formatDistanceToNow(new Date(data.latestReleaseDate), { addSuffix: true })}`
        : `You're on ${data.currentVersion}. Checked ${checkedAtCopy}.`;
      const notify = data.updateAvailable ? toast.info : toast.success;
      notify(data.updateAvailable ? "Update available" : "You're up to date", { description });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Unable to check for updates.";
      toast.error("Update check failed", { description: message });
    },
  });
  const printerStats = useMemo(() => {
    const printers = printersQuery.data?.printers ?? [];
    const online = printers.filter((printer) => printer.status === "online").length;
    return {
      total: printers.length,
      online,
    };
  }, [printersQuery.data?.printers]);
  const printerStatusLabel = useMemo(() => {
    if (printersQuery.isLoading) {
      return "Checking printer status…";
    }
    if (printersQuery.isError) {
      return "Printer status unavailable";
    }
    if (printerStats.total === 0) {
      return "No printers registered";
    }
    if (printerStats.online === printerStats.total) {
      return "All printers online";
    }
    if (printerStats.online === 0) {
      return "All printers offline";
    }
    return `${printerStats.online} of ${printerStats.total} online`;
  }, [printerStats.online, printerStats.total, printersQuery.isError, printersQuery.isLoading]);
  const printerStatusIndicatorClass = useMemo(() => {
    if (printersQuery.isLoading) {
      return "bg-amber-500";
    }
    if (printersQuery.isError) {
      return "bg-red-500";
    }
    if (printerStats.total === 0) {
      return "bg-gray-400";
    }
    if (printerStats.online === printerStats.total) {
      return "bg-emerald-500";
    }
    if (printerStats.online === 0) {
      return "bg-red-500";
    }
    return "bg-amber-500";
  }, [printerStats.online, printerStats.total, printersQuery.isError, printersQuery.isLoading]);
  const isLoadingSettings = (!hasHydrated && settingsLoading) || isResetting;
  const systemDiagnostics = systemDiagnosticsQuery.data;
  const diagnosticsIssues = systemDiagnostics?.issues ?? [];
  const systemHealthLabel = (() => {
    if (systemDiagnosticsQuery.isLoading && !systemDiagnostics) {
      return "Running diagnostics…";
    }
    if (systemDiagnosticsQuery.isError) {
      return "Diagnostics unavailable";
    }
    if (!systemDiagnostics) {
      return "Diagnostics pending";
    }
    return diagnosticsIssues.length > 0 ? "Attention required" : "All systems operational";
  })();
  const systemHealthDescription = (() => {
    if (systemDiagnosticsQuery.isLoading && !systemDiagnostics) {
      return "Collecting telemetry across services.";
    }
    if (systemDiagnosticsQuery.isError) {
      return "Unable to fetch diagnostics. Please retry.";
    }
    if (!systemDiagnostics) {
      return "Run diagnostics to validate background services.";
    }
    return diagnosticsIssues.length > 0 ? diagnosticsIssues[0] : `Verified ${formatRelativeTime(systemDiagnostics.timestamp)}`;
  })();
  const uptimeLabel = (() => {
    if (systemDiagnosticsQuery.isError) {
      return "—";
    }
    if (systemDiagnosticsQuery.isLoading && !systemDiagnostics) {
      return "Checking uptime…";
    }
    return formatUptimeDuration(systemDiagnostics?.metrics?.uptimeSeconds ?? null);
  })();
  const uptimeDetail = systemDiagnostics
    ? `${systemDiagnostics.metrics.pendingPrinterJobs} pending · ${systemDiagnostics.metrics.failedPrinterJobs} failed`
    : "Awaiting queue metrics.";
  const updateStatusDescription = lastUpdateCheck
    ? `${lastUpdateCheck.updateAvailable ? `Update ${lastUpdateCheck.latestVersion} available` : `On ${lastUpdateCheck.currentVersion}`}
       · Checked ${formatRelativeTime(lastUpdateCheck.checkedAt)}`
    : null;
  const securityOverview = securityOverviewQuery.data;
  const passwordCoverage = securityOverview?.passwordCoverage;
  const pinCoverage = securityOverview?.pinCoverage;
  const privacyRequestStats = securityOverview?.privacyRequests;
  const securityEventStats = securityOverview?.recentSecurityEvents;
  const credentialStatusLabel = securityOverviewQuery.isLoading
    ? "Checking…"
    : passwordCoverage
      ? `${passwordCoverage.withPassword}/${passwordCoverage.totalUsers} secured`
      : securityOverviewQuery.isError
        ? "Unavailable"
        : "No staff records";
  const credentialDescription = securityOverviewQuery.isError
    ? "Unable to load credential telemetry."
    : passwordCoverage
      ? passwordCoverage.totalUsers === 0
        ? "Invite staff to start enforcing password rotation."
        : passwordCoverage.coveragePct === 100
          ? "All staff members satisfy the strong password policy."
          : "Encourage remaining admins to update their passwords."
      : "Awaiting credential telemetry.";
  const pinBadgeVariant = pinCoverage
    ? pinCoverage.withPin === pinCoverage.totalStaff && pinCoverage.totalStaff > 0
      ? "default"
      : pinCoverage.withPin > 0
        ? "secondary"
        : "outline"
    : "outline";
  const pinBadgeLabel = pinCoverage
    ? pinCoverage.withPin === pinCoverage.totalStaff && pinCoverage.totalStaff > 0
      ? "Enforced"
      : pinCoverage.withPin > 0
        ? "Partial"
        : "Not configured"
    : securityOverviewQuery.isLoading
      ? "Loading…"
      : "Not configured";
  const pinDescription = securityOverviewQuery.isError
    ? "Unable to load PIN telemetry."
    : pinCoverage
      ? pinCoverage.totalStaff === 0
        ? "No staff profiles configured for PIN access."
        : pinCoverage.lastRotationAt
          ? `Last rotation ${formatRelativeTime(pinCoverage.lastRotationAt)}`
          : "Staff PINs have not been rotated yet."
      : "Awaiting PIN telemetry.";
  const privacyRequestsSummary = securityOverviewQuery.isError
    ? "Unable to load privacy queue."
    : privacyRequestStats
      ? `${privacyRequestStats.open} open · ${privacyRequestStats.completed} completed`
      : securityOverviewQuery.isLoading
        ? "Checking privacy queue…"
        : "No privacy requests logged.";
  const privacyLastRequestCopy = privacyRequestStats?.lastRequestAt
    ? formatRelativeTime(privacyRequestStats.lastRequestAt)
    : "No recent requests";
  const securityEventsSummary = securityOverviewQuery.isError
    ? "Unable to load security events."
    : securityEventStats
      ? `${securityEventStats.total} events in 30 days`
      : securityOverviewQuery.isLoading
        ? "Scanning audit logs…"
        : "No security events logged.";
  const securityLastEventCopy = securityEventStats?.lastEventAt
    ? formatRelativeTime(securityEventStats.lastEventAt)
    : "No recent events";

  const syncSettingsFromResponse = useCallback(
    (restaurant: RemoteRestaurantSettings) => {
      const preferences = restaurant.preferences ?? {
        notifications: defaultSettings.notifications,
        autoRefresh: defaultSettings.autoRefresh,
        soundAlerts: defaultSettings.soundAlerts,
        printerEnabled: defaultSettings.printerEnabled,
      backupEnabled: defaultSettings.backupEnabled,
      emailNotifications: defaultSettings.emailNotifications,
      smsAlerts: defaultSettings.smsAlerts,
      darkModeSchedule: defaultSettings.darkModeSchedule,
    };

    const normalizedCurrency = restaurant.currency?.trim().toUpperCase() || defaultSettings.currency;

      setSettings((prev) => ({
        ...prev,
        restaurantName: restaurant.name,
        contactEmail: restaurant.contactEmail ?? "",
        contactPhone: restaurant.contactPhone ?? "",
        address: restaurant.address ?? "",
        timezone: restaurant.timezone,
        currency: normalizedCurrency,
        taxMode: restaurant.taxMode,
        taxRate: percentToInputString(restaurant.taxRate),
        serviceCharge: percentToInputString(restaurant.serviceCharge),
        cardSurcharge: percentToInputString(restaurant.cardSurcharge),
        notifications: preferences.notifications,
        autoRefresh: preferences.autoRefresh,
        soundAlerts: preferences.soundAlerts,
        printerEnabled: preferences.printerEnabled,
        backupEnabled: preferences.backupEnabled,
        emailNotifications: preferences.emailNotifications,
        smsAlerts: preferences.smsAlerts,
        darkModeSchedule: preferences.darkModeSchedule,
      }));

      const {
        activeRestaurantId,
        activeSubdomain,
        activeRestaurantName,
        isDemo,
        setActiveTenant,
      } = useTenantStore.getState();

      const resolvedSubdomain = activeSubdomain ?? routeSubdomain ?? null;
      if (!resolvedSubdomain) {
        return;
      }

      const needsTenantUpdate =
        activeRestaurantId !== restaurant.id ||
        activeRestaurantName !== restaurant.name ||
        activeSubdomain !== resolvedSubdomain;

      if (needsTenantUpdate) {
        setActiveTenant(restaurant.id, resolvedSubdomain, restaurant.name, isDemo);
      }

      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(
            "restaurant_data",
            JSON.stringify({
              restaurant_id: restaurant.id,
              subdomain: resolvedSubdomain,
              name: restaurant.name,
            }),
          );
        } catch (storageError) {
          console.warn("[settings] failed to persist restaurant_data", storageError);
        }
      }
    },
    [routeSubdomain],
  );

  const handleProvisionSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!newHandheldName.trim() || provisionHandheldMutation.isPending) {
        return;
      }
      provisionHandheldMutation.mutate({
        name: newHandheldName.trim(),
        ...(newHandheldLocation.trim() ? { locationId: newHandheldLocation.trim() } : {}),
      });
    },
    [newHandheldLocation, newHandheldName, provisionHandheldMutation],
  );

  const handleRotateDevice = useCallback(
    (deviceId: string) => {
      if (rotateHandheldMutation.isPending) {
        return;
      }
      rotateHandheldMutation.mutate(deviceId);
    },
    [rotateHandheldMutation],
  );

  const handleRevokeDevice = useCallback(
    (deviceId: string) => {
      if (revokeHandheldMutation.isPending) {
        return;
      }
      if (typeof window !== "undefined" && !window.confirm("Revoke this handheld device?")) {
        return;
      }
      revokeHandheldMutation.mutate(deviceId);
    },
    [revokeHandheldMutation],
  );

  useEffect(() => {
    if (!serverSettings) {
      return;
    }
    syncSettingsFromResponse(serverSettings);
    setHasHydrated(true);
  }, [serverSettings, syncSettingsFromResponse]);

  useEffect(() => {
    if (!settingsError) {
      return;
    }
    const message =
      settingsErrorObject instanceof Error
        ? settingsErrorObject.message
        : "Please try again later.";
    toast.error("Unable to load restaurant settings", {
      description: message,
    });
  }, [settingsError, settingsErrorObject]);

  const handleSaveSettings = async (): Promise<boolean> => {
    const trimmedName = settings.restaurantName.trim();
    if (trimmedName.length === 0) {
      toast.error("Restaurant name is required", {
        description: "Enter your business name before saving.",
      });
      return false;
    }
    setIsSaving(true);
    try {
      const payload = buildUpdatePayload({ ...settings, restaurantName: trimmedName });
      const response = await updateRestaurantSettings(payload);
      if (response?.restaurant) {
        syncSettingsFromResponse(response.restaurant);
        queryClient.setQueryData(RESTAURANT_SETTINGS_QUERY_KEY, response);
        queryClient.invalidateQueries({ queryKey: ["layout", "restaurant", response.restaurant.id] });
        await invalidateRestaurantSettings();
      }
      toast.success("Settings saved successfully", {
        description: "All changes have been applied",
      });
      return true;
    } catch (error) {
      console.error("[settings] failed to save preferences", error);
      toast.error("Failed to save settings", {
        description:
          error instanceof TypeError && error.message === "Failed to fetch"
            ? "Could not reach the API server. Make sure the backend is running (npm run server) and the dev proxy points to the right port."
            : error instanceof Error
              ? error.message
              : "Please try again later.",
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetSettings = async () => {
    setIsResetting(true);
    setTheme("system");
    try {
      const payload = buildUpdatePayload({ ...defaultSettings });
      const response = await updateRestaurantSettings(payload);
      if (response.restaurant) {
        syncSettingsFromResponse(response.restaurant);
        queryClient.setQueryData(RESTAURANT_SETTINGS_QUERY_KEY, response);
        queryClient.invalidateQueries({ queryKey: ["layout", "restaurant", response.restaurant.id] });
        await invalidateRestaurantSettings();
      } else {
        setSettings({ ...defaultSettings });
      }
      toast.info("Settings reset to defaults");
    } catch (error) {
      console.error("[settings] failed to reset settings", error);
      setSettings({ ...defaultSettings });
      toast.error("Unable to reset settings", {
        description:
          error instanceof TypeError && error.message === "Failed to fetch"
            ? "Could not reach the API server. Make sure the backend is running and accessible."
            : error instanceof Error
              ? error.message
              : "Please try again later.",
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handleToggleSetting = (key: BooleanSettingKey) => {
    setSettings((prev) => {
      const nextValue = !prev[key];
      const label = booleanSettingLabels[key] ?? "Setting";
      toast.success(`${label} ${nextValue ? "enabled" : "disabled"}`);
      return { ...prev, [key]: nextValue };
    });
  };

  const handleInputChange = (key: StringSettingKey, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleQuickTaxModeChange = useCallback(
    (mode: TaxMode) => {
      setSettings((prev) => (prev.taxMode === mode ? prev : { ...prev, taxMode: mode }));
    },
    [setSettings],
  );

  const derivedPreferences = useMemo(() => {
    return {
      notifications: preferences?.notifications ?? settings.notifications,
      autoRefresh: preferences?.autoRefresh ?? settings.autoRefresh,
      soundAlerts: preferences?.soundAlerts ?? settings.soundAlerts,
      printerEnabled: preferences?.printerEnabled ?? settings.printerEnabled,
      backupEnabled: preferences?.backupEnabled ?? settings.backupEnabled,
      emailNotifications: preferences?.emailNotifications ?? settings.emailNotifications,
      smsAlerts: preferences?.smsAlerts ?? settings.smsAlerts,
      darkModeSchedule: preferences?.darkModeSchedule ?? settings.darkModeSchedule,
    };
  }, [preferences, settings]);

  const baselineTaxMode = serverSettings?.taxMode ?? defaultSettings.taxMode;
  const baselineCurrency = serverSettings?.currency ?? defaultSettings.currency;
  const hasPendingTaxModeChange = settings.taxMode !== baselineTaxMode;
  const hasPendingCurrencyChange = settings.currency !== baselineCurrency;

  const taxModeLabel = settings.taxMode === "inclusive" ? "Prices include tax" : "Tax added at checkout";
  const taxModeBadgeLabel = settings.taxMode === "inclusive" ? "Inclusive" : "Exclusive";
  const currencyDisplayLabel =
    currencyOptions.find((option) => option.value === settings.currency)?.label ?? settings.currency.toUpperCase();
  const taxRateDisplay = settings.taxRate?.trim().length ? settings.taxRate.trim() : "0";
  const serviceChargeDisplay =
    settings.serviceCharge?.trim().length && Number.parseFloat(settings.serviceCharge) > 0
      ? settings.serviceCharge.trim()
      : null;
  const cardSurchargeDisplay =
    settings.cardSurcharge?.trim().length && Number.parseFloat(settings.cardSurcharge) > 0
      ? settings.cardSurcharge.trim()
      : null;
  const printerLabel =
    printerStats.total === 0 ? "No printers connected" : `${printerStats.online}/${printerStats.total} online`;
  const notificationsLabel = derivedPreferences.notifications ? "Enabled" : "Disabled";

  const printerHelperText = useMemo(() => {
    const autoRefreshLabel = derivedPreferences.autoRefresh ? "Auto refresh is enabled." : "Auto refresh is off.";
    if (printersQuery.isFetching) {
      return `Checking printer connectivity... ${autoRefreshLabel}`;
    }
    if (printersQuery.isError) {
      const message = printersQuery.error instanceof Error ? ` ${printersQuery.error.message}` : "";
      return `Printer status unavailable${message ? ":" + message : ""}`;
    }
    if (printersQuery.dataUpdatedAt) {
      return `Last checked ${formatDistanceToNow(printersQuery.dataUpdatedAt, { addSuffix: true })}. ${autoRefreshLabel}`;
    }
    return `Printer status updates after the first check completes. ${autoRefreshLabel}`;
  }, [
    derivedPreferences.autoRefresh,
    printersQuery.dataUpdatedAt,
    printersQuery.isError,
    printersQuery.error,
    printersQuery.isFetching,
  ]);
  const printers = printersQuery.data?.printers ?? [];
  const formatPrinterLastSeen = useCallback((value: string | null | undefined) => {
    if (!value) {
      return "never";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return "never";
    }
    return formatDistanceToNow(parsed, { addSuffix: true });
  }, []);

  const statusCards: StatusCardConfig[] = [
    {
      key: "tax-mode",
      label: "Tax Mode",
      value: taxModeLabel,
      helperText:
        settings.taxMode === "inclusive"
          ? "Menu prices already include tax. Save changes to apply."
          : "Tax is calculated at checkout. Save changes to apply.",
      icon: Settings,
      gradientClass: "bg-gradient-to-br from-emerald-500/10 to-emerald-600/5",
      iconContainerClass: "bg-emerald-500/10 border border-emerald-500/20",
      iconClass: "text-emerald-600 dark:text-emerald-400",
      badges: [
        {
          label: taxModeBadgeLabel,
          className:
            "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20",
        },
        {
          label: `Tax rate ${taxRateDisplay}%`,
          className:
            "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20",
        },
        ...(serviceChargeDisplay
          ? [
              {
                label: `Service charge ${serviceChargeDisplay}%`,
                className:
                  "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20",
              },
            ]
          : []),
        ...(cardSurchargeDisplay
          ? [
              {
                label: `Card surcharge ${cardSurchargeDisplay}%`,
                className:
                  "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20",
              },
            ]
          : []),
        ...(hasPendingTaxModeChange
          ? [
              {
                label: "Unsaved change",
                className:
                  "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30 hover:bg-amber-500/25",
              },
            ]
          : []),
      ],
    },
    {
      key: "currency",
      label: "Currency",
      value: currencyDisplayLabel,
      helperText: "Displayed on menus, receipts, and payouts. Save changes to apply.",
      icon: DollarSign,
      gradientClass: "bg-gradient-to-br from-blue-500/10 to-blue-600/5",
      iconContainerClass: "bg-blue-500/10 border border-blue-500/20",
      iconClass: "text-blue-600 dark:text-blue-400",
      badges: [
        {
          label: "Default for orders",
          className:
            "bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-500/20 hover:bg-blue-500/20",
        },
        ...(hasPendingCurrencyChange
          ? [
              {
                label: "Unsaved change",
                className:
                  "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30 hover:bg-amber-500/25",
              },
            ]
          : []),
      ],
    },
    {
      key: "notifications",
      label: "Notifications",
      value: notificationsLabel,
      helperText: "Enable alerts for new orders and service issues.",
      icon: Bell,
      gradientClass: "bg-gradient-to-br from-purple-500/10 to-purple-600/5",
      iconContainerClass: "bg-purple-500/10 border border-purple-500/20",
      iconClass: "text-purple-600 dark:text-purple-400",
      badges: [
        {
          label: derivedPreferences.soundAlerts ? "Sound on" : "Muted",
          className:
            "bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-500/20 hover:bg-purple-500/20",
        },
        {
          label: derivedPreferences.emailNotifications ? "Email alerts on" : "Email alerts off",
          className:
            "bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-500/20 hover:bg-purple-500/20",
        },
      ],
    },
    {
      key: "printers",
      label: "Printers",
      value: printerLabel,
      helperText: printerHelperText,
      icon: Printer,
      gradientClass: "bg-gradient-to-br from-orange-500/10 to-orange-600/5",
      iconContainerClass: "bg-orange-500/10 border border-orange-500/20",
      iconClass: "text-orange-600 dark:text-orange-400",
      badges: [
        {
          label: derivedPreferences.printerEnabled ? "Printing enabled" : "Printing disabled",
          className:
            "bg-orange-500/10 text-orange-700 dark:text-orange-300 border border-orange-500/20 hover:bg-orange-500/20",
        },
        {
          label: derivedPreferences.autoRefresh ? "Auto refresh on" : "Auto refresh off",
          className:
            "bg-slate-500/10 text-slate-700 dark:text-slate-300 border border-slate-500/20 hover:bg-slate-500/20",
        },
        ...(printersQuery.isFetching
          ? [
              {
                label: "Refreshing",
                className:
                  "bg-blue-500/15 text-blue-700 dark:text-blue-400 border border-blue-500/30 hover:bg-blue-500/25",
              },
            ]
          : []),
      ],
    },
  ];

  const resolveTenantPath = useCallback(
    (path: string) => (routeSubdomain ? `/r/${routeSubdomain}${path}` : path),
    [routeSubdomain],
  );

  const administrationCards = [
    {
      title: "Restaurant Profile",
      description: "Manage business details, contact information, and operating hours",
      icon: Building2,
      iconContainerClass: "bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400",
      onClick: () => setRestaurantDialogOpen(true),
      manageAction: () => setRestaurantDialogOpen(true),
    },
    {
      title: "Payments",
      description: "Configure payment methods, tax rates, and billing settings",
      icon: CreditCard,
      iconContainerClass: "bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400",
      onClick: () => setPaymentsDialogOpen(true),
      manageAction: () => setPaymentsDialogOpen(true),
    },
    {
      title: "QR Codes",
      description: "Generate and manage QR codes for tables and menu access",
      icon: QrCode,
      iconContainerClass: "bg-purple-500/10 border border-purple-500/20 text-purple-600 dark:text-purple-400",
      onClick: () => navigate(resolveTenantPath("/qr-codes")),
      manageAction: () => navigate(resolveTenantPath("/qr-codes")),
    },
    {
      title: "Team",
      description: "Add team members, assign roles, and manage permissions",
      icon: Users,
      iconContainerClass: "bg-orange-500/10 border border-orange-500/20 text-orange-600 dark:text-orange-400",
      onClick: () => navigate(resolveTenantPath("/settings/staff")),
      manageAction: () => navigate(resolveTenantPath("/settings/staff")),
    },
  ];

  return (
    <div className="p-8 space-y-8">
      <div className="flex items-start justify-between">
        <div className="space-y-1.5">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            System Settings
          </h1>
          <p className="text-muted-foreground">Configure restaurant settings and system preferences</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleResetSettings}
            disabled={isLoadingSettings}
            className="gap-2 shadow-sm hover:shadow-md transition-all duration-200"
          >
            <RefreshCw className="h-4 w-4" />
            Reset
          </Button>
          <Button
            onClick={handleSaveSettings}
            disabled={isSaving || isLoadingSettings}
            className="gap-2 bg-foreground text-background hover:bg-foreground/90 dark:bg-white dark:text-gray-900 shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-70"
          >
            {isSaving ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {statusCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card
              key={card.key}
              className="group relative overflow-hidden border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className={`absolute inset-0 ${card.gradientClass} opacity-50 group-hover:opacity-70 transition-opacity duration-300`} />
              <CardContent className="relative z-10 flex flex-col gap-4 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="mb-1 text-sm text-muted-foreground">{card.label}</p>
                    <p className="text-2xl font-semibold text-foreground">{card.value}</p>
                  </div>
                  <div className={`rounded-lg p-2.5 ${card.iconContainerClass}`}>
                    <Icon className={`h-5 w-5 ${card.iconClass}`} />
                  </div>
                </div>

                {card.badges?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {card.badges.map((badge) => (
                      <Badge
                        key={`${card.key}-${badge.label}`}
                        className={
                          badge.className ??
                          "bg-white/10 text-white border border-white/20 backdrop-blur-sm hover:bg-white/20"
                        }
                      >
                        {badge.label}
                      </Badge>
                    ))}
                  </div>
                ) : null}

                {card.helperText ? (
                  <p className="text-xs text-muted-foreground">{card.helperText}</p>
                ) : null}

                {card.key === "tax-mode" && (
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant={settings.taxMode === "inclusive" ? "default" : "outline"}
                        onClick={() => handleQuickTaxModeChange("inclusive")}
                      >
                        Prices include tax
                      </Button>
                      <Button
                        size="sm"
                        variant={settings.taxMode === "exclusive" ? "default" : "outline"}
                        onClick={() => handleQuickTaxModeChange("exclusive")}
                      >
                        Add at checkout
                      </Button>
                    </div>
                    <Button
                      size="sm"
                      variant="link"
                      className="px-0 font-medium"
                      onClick={() => setPaymentsDialogOpen(true)}
                    >
                      Open payment settings
                    </Button>
                  </div>
                )}

                {card.key === "currency" && (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="status-currency-select" className="text-xs font-medium text-muted-foreground">
                        Default currency
                      </Label>
                      <Select value={settings.currency} onValueChange={(value) => handleInputChange("currency", value)}>
                        <SelectTrigger id="status-currency-select" className="h-9 w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {currencyOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      size="sm"
                      variant="link"
                      className="px-0 font-medium"
                      onClick={() => setPaymentsDialogOpen(true)}
                    >
                      Configure currency & tax
                    </Button>
                  </div>
                )}

                {card.key === "printers" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className={`h-2.5 w-2.5 rounded-full ${printerStatusIndicatorClass}`} />
                      <span>{printerStatusLabel}</span>
                    </div>

                    <div className="space-y-2">
                      {printersQuery.isLoading ? (
                        <p className="text-xs text-muted-foreground">Checking printers...</p>
                      ) : printers.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          No printers are connected yet. Connect a device and rescan to verify.
                        </p>
                      ) : (
                        <ul className="space-y-2">
                          {printers.slice(0, 3).map((printer) => (
                            <li
                              key={printer.id}
                              className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-background/70 px-3 py-2 text-xs text-muted-foreground"
                            >
                              <div>
                                <span className="flex items-center gap-2 text-foreground">
                                  <span className={`h-2.5 w-2.5 rounded-full ${PRINTER_STATUS_DOT[printer.status]}`} />
                                  {printer.name}
                                </span>
                                <span className="block text-[11px] text-muted-foreground">{printer.location}</span>
                              </div>
                              <div className="text-right text-[11px] text-muted-foreground">
                                <span className="block uppercase tracking-wide">
                                  {printer.status === "online"
                                    ? "Online"
                                    : printer.status === "offline"
                                    ? "Offline"
                                    : "Unknown"}
                                </span>
                                <span className="block">Last seen {formatPrinterLastSeen(printer.lastSeenAt)}</span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => printersQuery.refetch()}
                        disabled={printersQuery.isFetching}
                      >
                        {printersQuery.isFetching ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        Rescan
                      </Button>
                      <Button
                        size="sm"
                        variant="link"
                        className="px-0 font-medium"
                        onClick={() => navigate(resolveTenantPath("/settings/printers"))}
                      >
                        Manage printers
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="administration" className="space-y-6">
        <TabsList className="bg-gray-100/80 dark:bg-gray-800/50 border border-gray-200/60 dark:border-gray-700/60 p-1">
          <TabsTrigger
            value="administration"
            className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900 data-[state=active]:shadow-sm"
          >
            <Settings2 className="mr-2 h-4 w-4" />
            Administration
          </TabsTrigger>
          <TabsTrigger
            value="workspace"
            className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900 data-[state=active]:shadow-sm"
          >
            <Palette className="mr-2 h-4 w-4" />
            Workspace
          </TabsTrigger>
          <TabsTrigger
            value="notifications"
            className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900 data-[state=active]:shadow-sm"
          >
            <Bell className="mr-2 h-4 w-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger
            value="system"
            className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900 data-[state=active]:shadow-sm"
          >
            <Database className="mr-2 h-4 w-4" />
            System
          </TabsTrigger>
        </TabsList>

        <TabsContent value="administration" className="space-y-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {administrationCards.map((card) => {
              const Icon = card.icon;
              return (
                <Card
                  key={card.title}
                  className="group cursor-pointer border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-lg transition-all duration-300 hover:shadow-xl"
                  onClick={card.onClick}
                >
                  <CardContent className="p-6">
                    <div className="mb-4 flex items-start justify-between">
                      <div className={`rounded-lg p-3 ${card.iconContainerClass}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground transition-colors group-hover:text-foreground" />
                    </div>
                    <h3 className="mb-2 text-lg font-semibold text-foreground">{card.title}</h3>
                    <p className="mb-4 text-sm text-muted-foreground">{card.description}</p>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={(event) => {
                        event.stopPropagation();
                        (card.manageAction ?? card.onClick)();
                      }}
                    >
                      Manage
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card className="border-blue-500/20 bg-blue-500/5 dark:bg-blue-500/10">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                <div>
                  <p className="mb-1 text-sm font-medium text-foreground">Role-based Access Control</p>
                  <p className="text-xs text-muted-foreground">
                    Some settings may require administrator permissions. Contact your system administrator if you need
                    access to restricted features.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="workspace" className="space-y-6">
          <div className="space-y-4">
            <div>
              <h3 className="mb-1 text-lg font-semibold text-foreground">Appearance</h3>
              <p className="text-sm text-muted-foreground">Customize how the interface looks and feels</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {themeOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = theme === option.value;
                return (
                  <Card
                    key={option.value}
                    className={`cursor-pointer border-2 transition-all duration-300 ${
                      isSelected
                        ? "border-emerald-500 bg-emerald-500/5 dark:bg-emerald-500/10 shadow-lg"
                        : "border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600"
                    }`}
                    onClick={() => setTheme(option.value)}
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col items-center space-y-3 text-center">
                        <div
                          className={`rounded-lg p-3 ${
                            isSelected
                              ? "border-2 border-emerald-500/40 bg-emerald-500/20"
                              : "border-2 border-transparent bg-gray-100 dark:bg-gray-800"
                          }`}
                        >
                          <Icon
                            className={`h-6 w-6 ${
                              isSelected ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"
                            }`}
                          />
                        </div>
                        <div>
                          <h4 className="mb-1 font-semibold text-foreground">{option.title}</h4>
                          <p className="text-xs text-muted-foreground">{option.description}</p>
                        </div>
                        {isSelected && (
                          <Badge className="border border-emerald-500/30 bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                            Active
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          <Card className="border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-lg">
            <CardHeader className="border-b border-gray-200/60 dark:border-gray-700/60">
              <CardTitle className="flex items-center gap-2 text-foreground">
                <div className="rounded-lg border border-orange-500/20 bg-orange-500/10 p-2">
                  <Settings className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                </div>
                Preferences
              </CardTitle>
              <CardDescription>Adjust workspace defaults for your team</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-6">
              <div className="flex items-center justify-between rounded-lg border border-gray-200/60 bg-gray-50/50 p-4 dark:border-gray-700/60 dark:bg-gray-800/50">
                <div className="space-y-1">
                  <Label className="flex items-center gap-2 text-foreground">
                    <Bell className="h-4 w-4 text-muted-foreground" />
                    Notifications
                  </Label>
                  <p className="text-xs text-muted-foreground">Receive alerts for new orders and updates</p>
                </div>
                <Switch checked={settings.notifications} onCheckedChange={() => handleToggleSetting("notifications")} />
              </div>

              <div className="flex items-center justify-between rounded-lg border border-gray-200/60 bg-gray-50/50 p-4 dark:border-gray-700/60 dark:bg-gray-800/50">
                <div className="space-y-1">
                  <Label className="flex items-center gap-2 text-foreground">
                    <Volume2 className="h-4 w-4 text-muted-foreground" />
                    Sound Effects
                  </Label>
                  <p className="text-xs text-muted-foreground">Play sound for notifications and actions</p>
                </div>
                <Switch checked={settings.soundAlerts} onCheckedChange={() => handleToggleSetting("soundAlerts")} />
              </div>

              <div className="flex items-center justify-between rounded-lg border border-gray-200/60 bg-gray-50/50 p-4 dark:border-gray-700/60 dark:bg-gray-800/50">
                <div className="space-y-1">
                  <Label className="flex items-center gap-2 text-foreground">
                    <Zap className="h-4 w-4 text-muted-foreground" />
                    Auto Refresh
                  </Label>
                  <p className="text-xs text-muted-foreground">Automatically refresh dashboard data</p>
                </div>
                <Switch checked={settings.autoRefresh} onCheckedChange={() => handleToggleSetting("autoRefresh")} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card className="border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-lg">
              <CardHeader className="border-b border-gray-200/60 dark:border-gray-700/60">
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 p-2">
                    <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  Email Notifications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="flex items-center justify-between rounded-lg border border-gray-200/60 bg-gray-50/50 p-4 dark:border-gray-700/60 dark:bg-gray-800/50">
                  <div className="space-y-1">
                    <Label className="flex items-center gap-2 text-foreground">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      Email Alerts
                    </Label>
                    <p className="text-xs text-muted-foreground">Receive important updates via email</p>
                  </div>
                  <Switch
                    checked={settings.emailNotifications}
                    onCheckedChange={() => handleToggleSetting("emailNotifications")}
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border border-gray-200/60 bg-gray-50/50 p-4 dark:border-gray-700/60 dark:bg-gray-800/50">
                  <div className="space-y-1">
                    <Label className="flex items-center gap-2 text-foreground">
                      <Smartphone className="h-4 w-4 text-muted-foreground" />
                      SMS Alerts
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Receive text message alerts (carrier charges may apply)
                    </p>
                  </div>
                  <Switch checked={settings.smsAlerts} onCheckedChange={() => handleToggleSetting("smsAlerts")} />
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-lg">
              <CardHeader className="border-b border-gray-200/60 dark:border-gray-700/60">
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-2">
                    <BarChart3 className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  Messaging Health
                </CardTitle>
                <CardDescription>Last 24 hours</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                {notificationSummaryQuery.isLoading ? (
                  <p className="text-sm text-muted-foreground">Loading deliverability metrics…</p>
                ) : notificationSummaryQuery.isError ? (
                  <p className="text-sm text-red-500">Unable to load notification metrics.</p>
                ) : notificationSummary ? (
                  <>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <div className="rounded-lg border border-gray-200/60 bg-gray-50/50 p-4 text-sm dark:border-gray-700/60 dark:bg-gray-800/50">
                        <p className="text-muted-foreground">Messages Sent</p>
                        <p className="text-2xl font-semibold text-foreground">
                          {notificationSummary.totals.overallSent.toLocaleString()}
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-200/60 bg-gray-50/50 p-4 text-sm dark:border-gray-700/60 dark:bg-gray-800/50">
                        <p className="text-muted-foreground">Deliverability</p>
                        <p className="text-2xl font-semibold text-foreground">
                          {notificationSummary.totals.deliverabilityRate.toFixed(1)}%
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-200/60 bg-gray-50/50 p-4 text-sm dark:border-gray-700/60 dark:bg-gray-800/50">
                        <p className="text-muted-foreground">Bounce Rate</p>
                        <p className="text-2xl font-semibold text-foreground">
                          {notificationSummary.totals.bounceRate.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {([
                        { key: "email", label: "Email", accent: "text-blue-600 dark:text-blue-400" },
                        { key: "sms", label: "SMS", accent: "text-emerald-600 dark:text-emerald-400" },
                      ] as const).map(({ key, label, accent }) => {
                        const channel = notificationSummary.byChannel[key];
                        return (
                          <div
                            key={key}
                            className="rounded-lg border border-gray-200/60 bg-gray-50/50 p-4 dark:border-gray-700/60 dark:bg-gray-800/50"
                          >
                            <p className="text-sm text-muted-foreground">{label}</p>
                            <div className="mt-2 flex items-baseline gap-3">
                              <span className={`text-2xl font-semibold ${accent}`}>
                                {channel.sent.toLocaleString()}
                              </span>
                              <span className="text-xs text-muted-foreground">sent</span>
                            </div>
                            <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                              <div>
                                <p className="font-medium text-foreground">Delivered</p>
                                <p>{channel.delivered.toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="font-medium text-foreground">Failed</p>
                                <p>{channel.failed.toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="font-medium text-foreground">Bounced</p>
                                <p>{channel.bounced.toLocaleString()}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">No messaging activity recorded yet.</p>
                )}
              </CardContent>
            </Card>

            <Card className="border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-lg">
              <CardHeader className="border-b border-gray-200/60 dark:border-gray-700/60">
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <div className="rounded-lg border border-purple-500/20 bg-purple-500/10 p-2">
                    <Bell className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  Push Notifications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="rounded-lg border border-gray-200/60 bg-gray-50/50 p-4 dark:border-gray-700/60 dark:bg-gray-800/50">
                  <p className="mb-3 text-sm text-foreground">Enable push notifications for:</p>
                  <div className="space-y-3">
                    {[
                      { label: "New orders", defaultChecked: true },
                      { label: "Order updates", defaultChecked: true },
                      { label: "Payment confirmations", defaultChecked: false },
                      { label: "System alerts", defaultChecked: false },
                    ].map((option) => (
                      <Label
                        key={option.label}
                        className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border border-gray-300 text-primary focus:ring-primary"
                          defaultChecked={option.defaultChecked}
                        />
                        {option.label}
                      </Label>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="system" className="space-y-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card className="border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-lg">
              <CardHeader className="border-b border-gray-200/60 dark:border-gray-700/60">
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-2">
                    <Printer className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  Hardware
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="flex items-center justify-between rounded-lg border border-gray-200/60 bg-gray-50/50 p-4 dark:border-gray-700/60 dark:bg-gray-800/50">
                  <div className="space-y-1">
                    <Label className="flex items-center gap-2 text-foreground">
                      <Printer className="h-4 w-4 text-muted-foreground" />
                      Kitchen Printer
                    </Label>
                    <p className="text-xs text-muted-foreground">Auto-print orders to kitchen printer</p>
                  </div>
                  <Switch
                    checked={settings.printerEnabled}
                    onCheckedChange={() => handleToggleSetting("printerEnabled")}
                  />
                </div>

                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate(resolveTenantPath("/settings/printers"))}
                  >
                    <Printer className="mr-2 h-4 w-4" />
                    Manage Printers
                  </Button>
                </div>

                <div className="rounded-lg border border-gray-200/60 bg-gray-50/50 p-4 dark:border-gray-700/60 dark:bg-gray-800/50">
                  <div className="flex items-start gap-3">
                    <Wifi className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="mb-1 text-sm text-foreground">Printer Health</p>
                      <div className="flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${printerStatusIndicatorClass}`} />
                        <span className="text-xs text-muted-foreground">{printerStatusLabel}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-lg">
              <CardHeader className="border-b border-gray-200/60 dark:border-gray-700/60">
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 p-2">
                    <Cloud className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  Data & Backup
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="flex items-center justify-between rounded-lg border border-gray-200/60 bg-gray-50/50 p-4 dark:border-gray-700/60 dark:bg-gray-800/50">
                  <div className="space-y-1">
                    <Label className="flex items-center gap-2 text-foreground">
                      <Cloud className="h-4 w-4 text-muted-foreground" />
                      Auto Backup
                    </Label>
                    <p className="text-xs text-muted-foreground">Daily automatic backup to cloud storage</p>
                  </div>
                  <Switch checked={settings.backupEnabled} onCheckedChange={() => handleToggleSetting("backupEnabled")} />
                </div>

                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Nightly backups run automatically. Need an on-demand export? Generate a CSV from the reports
                    workspace.
                  </p>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate(resolveTenantPath("/reports"))}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Open Reports &amp; Exports
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-lg lg:col-span-2">
              <CardHeader className="border-b border-gray-200/60 dark:border-gray-700/60">
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-2">
                    <Smartphone className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  Handheld Devices
                </CardTitle>
                <CardDescription>
                  Manage handheld tokens and monitor device activity. Rotate tokens after each provisioning to keep connections secure.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {handheldDevicesQuery.isLoading ? (
                  <div className="rounded-md border border-dashed border-border/60 p-6 text-sm text-muted-foreground">
                    Loading handheld devices…
                  </div>
                ) : handheldDevicesQuery.isError ? (
                  <div className="rounded-md border border-destructive/30 bg-destructive/10 p-6 text-sm text-destructive">
                    Unable to load handheld devices. Please try again later.
                  </div>
                ) : (
                  <HandheldDeviceManager
                    devices={handheldDevices}
                    onProvision={() => setHandheldDialogOpen(true)}
                    onRotate={handleRotateDevice}
                    onRevoke={handleRevokeDevice}
                  />
                )}
              </CardContent>
            </Card>

            <Card className="border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-lg lg:col-span-2">
              <CardHeader className="border-b border-gray-200/60 dark:border-gray-700/60">
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <div className="rounded-lg border border-purple-500/20 bg-purple-500/10 p-2">
                    <Settings2 className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  System Maintenance
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => clearCacheMutation.mutate()}
                    disabled={clearCacheMutation.isPending}
                  >
                    {clearCacheMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Clearing cache…
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Clear Cache
                      </>
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => runDiagnosticsMutation.mutate()}
                    disabled={runDiagnosticsMutation.isPending}
                  >
                    {runDiagnosticsMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Running diagnostics…
                      </>
                    ) : (
                      <>
                        <Shield className="mr-2 h-4 w-4" />
                        Run Diagnostics
                      </>
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => updateCheckMutation.mutate()}
                    disabled={updateCheckMutation.isPending}
                  >
                    {updateCheckMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Checking updates…
                      </>
                    ) : (
                      <>
                        <Download className="mr-2 h-4 w-4" />
                        Check for Updates
                      </>
                    )}
                  </Button>
                </div>
                {updateStatusDescription ? (
                  <p className="mt-2 text-xs text-muted-foreground">{updateStatusDescription}</p>
                ) : null}

                <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle2
                        className={`mt-0.5 h-5 w-5 flex-shrink-0 ${diagnosticsIssues.length > 0 ? "text-orange-500 dark:text-orange-400" : "text-emerald-600 dark:text-emerald-400"}`}
                      />
                      <div>
                        <p className="mb-1 text-sm text-foreground">{systemHealthLabel}</p>
                        <p className="text-xs text-muted-foreground">{systemHealthDescription}</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200/60 bg-gray-50/50 p-3 dark:border-gray-700/60 dark:bg-gray-800/50">
                    <div className="flex items-center justify-between text-xs text-foreground">
                      <span className="text-muted-foreground">Uptime</span>
                      <span>{uptimeLabel}</span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{uptimeDetail}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-lg lg:col-span-2">
              <CardHeader className="border-b border-gray-200/60 dark:border-gray-700/60">
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-2">
                    <Shield className="h-4 w-4 text-red-600 dark:text-red-400" />
                  </div>
                  Security & Privacy
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="rounded-lg border border-gray-200/60 bg-gray-50/50 p-4 dark:border-gray-700/60 dark:bg-gray-800/50">
                    <div className="flex items-start gap-3">
                      <Lock className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground" />
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center justify-between text-sm text-foreground">
                          <span>Credential Coverage</span>
                          <span className="text-xs text-muted-foreground">{credentialStatusLabel}</span>
                        </div>
                        <Progress value={passwordCoverage?.coveragePct ?? 0} className="h-2" />
                        <p className="text-xs text-muted-foreground">{credentialDescription}</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200/60 bg-gray-50/50 p-4 dark:border-gray-700/60 dark:bg-gray-800/50">
                    <div className="flex items-start gap-3">
                      <Shield className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground" />
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center justify-between text-sm text-foreground">
                          <span>Two-Factor / PIN</span>
                          <Badge variant={pinBadgeVariant} size="xs">
                            {pinBadgeLabel}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {pinCoverage
                            ? `${pinCoverage.withPin}/${pinCoverage.totalStaff || 0} staff have PIN access`
                            : securityOverviewQuery.isLoading
                              ? "Checking staff profiles…"
                              : "No staff profiles configured."}
                        </p>
                        <p className="text-xs text-muted-foreground">{pinDescription}</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-orange-500/20 bg-orange-500/5 p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-600 dark:text-orange-400" />
                      <div className="flex-1 space-y-1">
                        <p className="text-sm text-foreground">Privacy Requests</p>
                        <p className="text-xs text-muted-foreground">{privacyRequestsSummary}</p>
                        <p className="text-xs text-muted-foreground">Last request: {privacyLastRequestCopy}</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200/60 bg-gray-50/50 p-4 dark:border-gray-700/60 dark:bg-gray-800/50">
                    <div className="flex items-start gap-3">
                      <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground" />
                      <div className="flex-1 space-y-1">
                        <p className="text-sm text-foreground">Security Events</p>
                        <p className="text-xs text-muted-foreground">{securityEventsSummary}</p>
                        <p className="text-xs text-muted-foreground">Last event: {securityLastEventCopy}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={handheldDialogOpen} onOpenChange={(open) => {
        if (!provisionHandheldMutation.isPending) {
          setHandheldDialogOpen(open);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Provision handheld device</DialogTitle>
            <DialogDescription>
              Generate a provisioning token for a new handheld. Copy the token immediately after creation-it will only be shown once.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleProvisionSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="handheld-name">Device label</Label>
              <Input
                id="handheld-name"
                value={newHandheldName}
                onChange={(event) => setNewHandheldName(event.target.value)}
                placeholder="Runner handheld"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="handheld-location">Location ID (optional)</Label>
              <Input
                id="handheld-location"
                value={newHandheldLocation}
                onChange={(event) => setNewHandheldLocation(event.target.value)}
                placeholder="Location ID"
              />
              <p className="text-xs text-muted-foreground">
                Leave blank to allow this handheld to access all locations.
              </p>
            </div>
            <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setHandheldDialogOpen(false)}
                disabled={provisionHandheldMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!newHandheldName.trim() || provisionHandheldMutation.isPending}>
                {provisionHandheldMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Provision device
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={restaurantDialogOpen} onOpenChange={setRestaurantDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              Restaurant Profile
            </DialogTitle>
            <DialogDescription>Update your restaurant&apos;s business information and contact details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="restaurant-name" className="flex items-center gap-2 text-foreground">
                <Tag className="h-4 w-4 text-muted-foreground" />
                Restaurant Name
              </Label>
              <Input
                id="restaurant-name"
                value={settings.restaurantName}
                onChange={(event) => handleInputChange("restaurantName", event.target.value)}
                placeholder="Enter restaurant name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-email" className="flex items-center gap-2 text-foreground">
                <Mail className="h-4 w-4 text-muted-foreground" />
                Contact Email
              </Label>
              <Input
                id="contact-email"
                type="email"
                value={settings.contactEmail}
                onChange={(event) => handleInputChange("contactEmail", event.target.value)}
                placeholder="contact@restaurant.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-phone" className="flex items-center gap-2 text-foreground">
                <Phone className="h-4 w-4 text-muted-foreground" />
                Contact Phone
              </Label>
              <Input
                id="contact-phone"
                type="tel"
                value={settings.contactPhone}
                onChange={(event) => handleInputChange("contactPhone", event.target.value)}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address" className="flex items-center gap-2 text-foreground">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                Address
              </Label>
              <Input
                id="address"
                value={settings.address}
                onChange={(event) => handleInputChange("address", event.target.value)}
                placeholder="Street address"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="restaurant-timezone" className="flex items-center gap-2 text-foreground">
                <Clock className="h-4 w-4 text-muted-foreground" />
                Timezone
              </Label>
              <Select value={settings.timezone} onValueChange={(value) => handleInputChange("timezone", value)}>
                <SelectTrigger id="restaurant-timezone">
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {timezoneOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestaurantDialogOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const saved = await handleSaveSettings();
                if (saved) {
                  setRestaurantDialogOpen(false);
                }
              }}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={paymentsDialogOpen} onOpenChange={setPaymentsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              Payment Settings
            </DialogTitle>
            <DialogDescription>Configure tax rates, service charges, and payment methods</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="currency" className="flex items-center gap-2 text-foreground">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  Currency
                </Label>
                <Select value={settings.currency} onValueChange={(value) => handleInputChange("currency", value)}>
                  <SelectTrigger id="currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencyOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tax-rate" className="text-foreground">
                  Tax Rate (%)
                </Label>
                <Input
                  id="tax-rate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.taxRate}
                  onChange={(event) => handleInputChange("taxRate", event.target.value)}
                  placeholder="8.5"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="service-charge" className="text-foreground">
                Service Charge (%)
              </Label>
              <Input
                id="service-charge"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={settings.serviceCharge}
                onChange={(event) => handleInputChange("serviceCharge", event.target.value)}
                placeholder="0"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="card-surcharge" className="text-foreground">
                Card Surcharge (%)
              </Label>
              <Input
                id="card-surcharge"
                type="number"
                min="0"
                max="4"
                step="0.1"
                value={settings.cardSurcharge}
                onChange={(event) => handleInputChange("cardSurcharge", event.target.value)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">
                Applied only to card and mobile wallet payments. 4% maximum per card-network rules.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tax-mode" className="text-foreground">
                Tax Calculation Mode
              </Label>
              <Select
                value={settings.taxMode}
                onValueChange={(value) => setSettings((prev) => ({ ...prev, taxMode: value as TaxMode }))}
              >
                <SelectTrigger id="tax-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="exclusive">Add tax on checkout</SelectItem>
                  <SelectItem value="inclusive">Prices include tax</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-4">
              <p className="text-xs text-muted-foreground">
                Tax and service charges will be automatically calculated and displayed on all invoices and receipts.
              </p>
            </div>
            <div className="rounded-lg border border-emerald-400/25 bg-emerald-400/10 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">Loyalty program</p>
                  <p className="text-xs text-muted-foreground">Manage earn modes and per-location overrides.</p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setLoyaltyDialogOpen(true)}
                  className="whitespace-nowrap"
                >
                  Manage loyalty
                </Button>
              </div>
            </div>

            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">Discount approvals & workflows</p>
                  <p className="text-xs text-muted-foreground">Edit schema-driven discount policies with live validation and previews.</p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setFinancialProfilesDialogOpen(true)}
                  className="whitespace-nowrap"
                >
                  Open editor
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentsDialogOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const saved = await handleSaveSettings();
                if (saved) {
                  setPaymentsDialogOpen(false);
                }
              }}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={loyaltyDialogOpen} onOpenChange={setLoyaltyDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              Loyalty program configuration
            </DialogTitle>
            <DialogDescription>Adjust tenant defaults and per-location overrides for loyalty earnings.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto pr-1">
            <LoyaltyProgramManager onClose={() => setLoyaltyDialogOpen(false)} />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={financialProfilesDialogOpen} onOpenChange={setFinancialProfilesDialogOpen}>
        <DialogContent className="max-w-5xl lg:max-w-6xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              Discount policies & workflows
            </DialogTitle>
            <DialogDescription>Review discount policies and approval workflows with live validation.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto pr-1">
            <FinancialProfilesManager onSaved={() => setFinancialProfilesDialogOpen(false)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinancialProfilesDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              QR Code Management
            </DialogTitle>
            <DialogDescription>Generate and manage QR codes for your restaurant</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Visit the QR Management page to create, customize, and download QR codes for your tables and menu.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQrDialogOpen(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                setQrDialogOpen(false);
                navigate(resolveTenantPath("/qr-codes"));
              }}
            >
              Go to QR Management
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              Team Management
            </DialogTitle>
            <DialogDescription>Manage team members and their roles</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Visit the Staff page to add team members, assign roles, and manage permissions.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTeamDialogOpen(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                setTeamDialogOpen(false);
                navigate(resolveTenantPath("/settings/staff"));
              }}
            >
              Go to Staff Management
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPageV3;
